import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiSearch, FiX, FiArrowRight, FiClock, FiTrendingUp } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { Title, Meta, Link } from "react-head";

import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientCategoryList from "../components/Client/ClientCategoryList";
import SmartImage from "../components/Client/SmartImage";
import ClientAdSense from "../components/Client/ClientAdSense";
import ClientCalling from "../api/ClientCalling";
import { formatView } from "../components/formatView";

const TRENDING = [
    "Cinematic",
    "Cyberpunk",
    "Anime",
    "Portrait",
    "3D Model",
    "Saree",
    "Baby",
    "Diwali",
];

const RECENT_KEY = "picpromptify_recent_searches";

const CardSkeleton = () => (
    <div className='relative aspect-[5/6] rounded-2xl ios-shimmer overflow-hidden'>
        <div
            className='absolute top-2 left-2 w-10 h-4 rounded-full'
            style={{ background: "rgba(255,255,255,0.08)" }}
        />
        <div className='absolute inset-x-2 bottom-2 hidden md:block'>
            <div
                className='h-9 rounded-lg'
                style={{
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(8px)",
                }}
            />
        </div>
    </div>
);

const ResultCard = ({ item, index, onClick }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            duration: 0.5,
            delay: Math.min(index * 0.03, 0.3),
            ease: [0.22, 1, 0.36, 1],
        }}
        whileHover={{ y: -5 }}
        whileTap={{ scale: 0.97 }}
        onClick={onClick}
        className='relative aspect-[5/6] rounded-2xl cursor-pointer group bg-client-back'
        style={{
            overflow: "clip",
            boxShadow:
                "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
    >
        <SmartImage
            src={item.l_image}
            alt={item.l_name}
            className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
        />

        <div
            className='absolute inset-x-0 bottom-0 h-3/5 pointer-events-none'
            style={{
                background:
                    "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, transparent 100%)",
            }}
        />

        <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
            <FiEye className='text-[10px] md:text-xs' />
            <span className='tabular-nums'>{formatView(item.l_view)}</span>
        </div>

        <div className='absolute inset-x-0 bottom-0 p-2 md:p-3 flex items-center justify-between gap-2'>
            <p
                className='text-white text-[10px] md:text-xs font-semibold truncate flex-1'
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}
            >
                {item.l_name}
            </p>
            <div className='ios-glass-btn hidden md:flex px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-medium text-white items-center gap-1 whitespace-nowrap'>
                Get Prompt
            </div>
        </div>

        <div
            className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
            style={{
                boxShadow:
                    "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)",
            }}
        />
    </motion.div>
);

const ClientSearch = () => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const observerTarget = useRef(null);

    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [listData, setListData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [nextPage, setNextPage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalList, setTotalList] = useState(0);
    const [recents, setRecents] = useState([]);
    const [focused, setFocused] = useState(false);

    // Load recents
    useEffect(() => {
        try {
            const raw = localStorage.getItem(RECENT_KEY);
            if (raw) setRecents(JSON.parse(raw));
        } catch {}
    }, []);

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Reset on new query
    useEffect(() => {
        setListData([]);
        setPage(1);
        setNextPage(null);
    }, [debounced]);

    const fetchData = useCallback(
        async (pageNum) => {
            try {
                if (pageNum === 1) setLoading(true);
                else setLoadingMore(true);

                const resp = await ClientCalling?.searchBar(debounced, pageNum, limit);
                if (resp?.status) {
                    setListData((prev) =>
                        pageNum === 1
                            ? Array.isArray(resp.data)
                                ? resp.data
                                : []
                            : [...prev, ...(Array.isArray(resp.data) ? resp.data : [])]
                    );
                    setNextPage(resp.nextPage || null);
                    setTotalList(resp.totalRecords || 0);
                } else if (pageNum === 1) {
                    setListData([]);
                    setTotalList(0);
                }
            } catch (err) {
                console.error("Search fetch failed:", err);
                if (pageNum === 1) setListData([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [debounced, limit]
    );

    useEffect(() => {
        if (debounced) fetchData(page);
    }, [page, debounced, fetchData]);

    // Save to recents when a query lands with results
    useEffect(() => {
        if (debounced && !loading && listData.length > 0) {
            setRecents((prev) => {
                const next = [debounced, ...prev.filter((r) => r !== debounced)].slice(0, 6);
                try {
                    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
                } catch {}
                return next;
            });
        }
    }, [debounced, loading, listData.length]);

    // Infinite scroll
    useEffect(() => {
        const el = observerTarget.current;
        if (!el || !nextPage) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    nextPage &&
                    !loading &&
                    !loadingMore
                ) {
                    setPage(nextPage);
                }
            },
            { rootMargin: "300px", threshold: 0 }
        );
        obs.observe(el);
        return () => obs.unobserve(el);
    }, [nextPage, loading, loadingMore]);

    const handleChip = (term) => {
        setSearch(term);
        inputRef.current?.focus();
    };

    const clearRecents = () => {
        setRecents([]);
        try {
            localStorage.removeItem(RECENT_KEY);
        } catch {}
    };

    const renderSEO = () => {
        const title = debounced
            ? `Search results for "${debounced}" | PicPromptify`
            : `Search Avatars | PicPromptify`;
        const description = debounced
            ? `Find avatars matching "${debounced}" on PicPromptify. Explore AI-generated avatars quickly.`
            : `Search AI-generated avatars on PicPromptify. Explore thousands of avatars.`;
        return (
            <>
                <Title>{title}</Title>
                <Meta name='description' content={description} />
                <Meta
                    name='keywords'
                    content={`avatars, AI avatars, ${debounced || ""}`}
                />
                <Meta property='og:title' content={title} />
                <Meta property='og:description' content={description} />
                <Meta property='og:type' content='website' />
                <Link
                    rel='canonical'
                    href={`https://picpromptify.com/search${
                        debounced ? `?q=${encodeURIComponent(debounced)}` : ""
                    }`}
                />
            </>
        );
    };

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            {renderSEO()}
            <ClientNavbar />
            <FixedButton />

            {/* HERO + SEARCH BAR */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 50, -30, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-80 h-80 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-4xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-10'>
                    {/* DSPY1 — top-of-page display ad above the hero */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className='text-center mb-6 md:mb-10'
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>
                                Discover Prompts
                            </span>
                        </div>
                        <h1 className='text-3xl md:text-5xl font-bold text-client-font leading-tight tracking-tight'>
                            Find your perfect{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                avatar
                            </span>
                        </h1>
                        <p className='text-client-font/55 text-sm md:text-base mt-3 max-w-xl mx-auto'>
                            Search thousands of AI-generated prompts by style, mood, or
                            theme.
                        </p>
                    </motion.div>

                    {/* Search input */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className='relative'
                    >
                        <div
                            className='flex items-center gap-2 md:gap-3 rounded-2xl px-3 md:px-4 py-2 md:py-2.5'
                            style={{
                                background: "rgba(255,255,255,0.06)",
                                backdropFilter: "blur(22px) saturate(180%)",
                                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                                border: focused
                                    ? "1px solid rgba(127,0,255,0.6)"
                                    : "1px solid rgba(255,255,255,0.14)",
                                boxShadow: focused
                                    ? "0 14px 40px -10px rgba(127,0,255,0.5), inset 0 1px 0 rgba(255,255,255,0.16)"
                                    : "0 10px 28px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)",
                                transition: "border 0.25s, box-shadow 0.25s",
                            }}
                        >
                            <FiSearch
                                className='text-lg md:text-xl flex-shrink-0'
                                style={{ color: focused ? "#FCFA02" : "rgba(255,255,255,0.6)" }}
                            />
                            <input
                                ref={inputRef}
                                type='text'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setFocused(true)}
                                onBlur={() => setFocused(false)}
                                placeholder='Search by style, mood, or theme…'
                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/40 text-sm md:text-base py-2'
                            />
                            <AnimatePresence>
                                {search && (
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.7 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.7 }}
                                        whileTap={{ scale: 0.9 }}
                                        whileHover={{ rotate: 90 }}
                                        onClick={() => setSearch("")}
                                        aria-label='Clear'
                                        className='w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                                        style={{
                                            background: "rgba(255,255,255,0.08)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            color: "rgba(255,255,255,0.75)",
                                        }}
                                    >
                                        <FiX className='text-sm' />
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>

                    {/* Chips: Recent + Trending */}
                    {!debounced && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className='mt-6 space-y-4'
                        >
                            {recents.length > 0 && (
                                <div>
                                    <div className='flex items-center justify-between mb-2'>
                                        <div className='inline-flex items-center gap-1.5 text-client-font/55 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold'>
                                            <FiClock />
                                            Recent
                                        </div>
                                        <button
                                            onClick={clearRecents}
                                            className='text-client-font/45 hover:text-client-font text-[10px] md:text-xs transition-colors'
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div className='flex flex-wrap gap-2'>
                                        {recents.map((r) => (
                                            <motion.button
                                                key={r}
                                                whileHover={{ y: -2, scale: 1.04 }}
                                                whileTap={{ scale: 0.96 }}
                                                onClick={() => handleChip(r)}
                                                className='ios-glass-btn px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                                            >
                                                {r}
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className='inline-flex items-center gap-1.5 text-client-font/55 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-2'>
                                    <FiTrendingUp style={{ color: "#FCFA02" }} />
                                    Trending
                                </div>
                                <div className='flex flex-wrap gap-2'>
                                    {TRENDING.map((t, i) => (
                                        <motion.button
                                            key={t}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.25 + i * 0.04 }}
                                            whileHover={{ y: -2, scale: 1.04 }}
                                            whileTap={{ scale: 0.96 }}
                                            onClick={() => handleChip(t)}
                                            className='px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium relative overflow-hidden group'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(127,0,255,0.18), rgba(252,250,2,0.08))",
                                                border: "1px solid rgba(255,255,255,0.12)",
                                            }}
                                        >
                                            <span className='relative z-10'>{t}</span>
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* RESULTS / CATEGORY LIST */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-7xl'>
                    {/* Empty state (no search yet) — show category list */}
                    {!debounced && (
                        <div className='mt-2 md:mt-4'>
                            <ClientCategoryList showRandom={false} />
                        </div>
                    )}

                    {/* Results header */}
                    {debounced && !loading && listData.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='mb-5 md:mb-7 flex items-center justify-between gap-3'
                        >
                            <div className='flex items-center gap-2 min-w-0'>
                                <div
                                    className='w-1 h-7 rounded-full flex-shrink-0'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <div className='min-w-0'>
                                    <p className='text-client-font/55 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold'>
                                        Results for
                                    </p>
                                    <h2 className='text-client-font font-bold text-base md:text-2xl tracking-tight truncate'>
                                        "{debounced}"
                                    </h2>
                                </div>
                            </div>
                            <div
                                className='ios-glass px-3 py-1.5 rounded-full text-client-font/85 text-[10px] md:text-xs flex items-center gap-1.5 whitespace-nowrap'
                            >
                                <span className='font-bold tabular-nums text-client-title'>
                                    {totalList}
                                </span>
                                <span className='text-client-font/65'>avatars</span>
                            </div>
                        </motion.div>
                    )}

                    {/* Initial loading */}
                    {debounced && loading && page === 1 && (
                        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                            {[...Array(10)].map((_, i) => (
                                <CardSkeleton key={i} />
                            ))}
                        </div>
                    )}

                    {/* No results */}
                    {debounced && !loading && listData.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center justify-center text-center py-16 md:py-24'
                        >
                            <div
                                className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5'
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    boxShadow:
                                        "0 12px 28px -10px rgba(127,0,255,0.45)",
                                }}
                            >
                                <FiSearch className='text-2xl md:text-3xl text-client-title' />
                            </div>
                            <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                                No results for "{debounced}"
                            </h3>
                            <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                                Try a different keyword or browse our trending picks below.
                            </p>
                            <div className='mt-6 flex flex-wrap justify-center gap-2'>
                                {TRENDING.slice(0, 5).map((t) => (
                                    <motion.button
                                        key={t}
                                        whileHover={{ y: -2, scale: 1.04 }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() => handleChip(t)}
                                        className='ios-glass-btn px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                                    >
                                        {t}
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Results grid */}
                    {debounced && listData.length > 0 && (
                        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                            {listData.map((item, idx) => (
                                <ResultCard
                                    key={item._id}
                                    item={item}
                                    index={idx}
                                    onClick={() => navigate(`/list/${item._id}`)}
                                />
                            ))}
                            {loadingMore &&
                                [...Array(5)].map((_, i) => (
                                    <CardSkeleton key={`s-${i}`} />
                                ))}
                        </div>
                    )}

                    {/* Infinite scroll trigger */}
                    {debounced && nextPage && (
                        <div
                            ref={observerTarget}
                            className='flex justify-center items-center py-8 md:py-12'
                        >
                            {loadingMore && (
                                <div className='ios-glass-dark inline-flex items-center gap-3 px-5 py-3 rounded-full'>
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                            duration: 0.9,
                                            repeat: Infinity,
                                            ease: "linear",
                                        }}
                                        className='w-4 h-4 rounded-full border-2 border-client-font/30 border-t-client-primary'
                                    />
                                    <span className='text-client-font text-xs md:text-sm font-medium tracking-wide'>
                                        Loading more
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* End of list */}
                    {debounced &&
                        !nextPage &&
                        !loading &&
                        listData.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className='flex flex-col items-center gap-2 py-10 text-client-font/40'
                            >
                                <HiSparkles
                                    className='text-2xl'
                                    style={{ color: "#7F00FF" }}
                                />
                                <p className='text-xs md:text-sm tracking-wider uppercase'>
                                    You've reached the end
                                </p>
                            </motion.div>
                        )}
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ClientSearch;
